import * as React from 'react'
import { EntityInformationListItem as ListItem } from '@user/components/EntityCards/atoms/EntityInformationListItem'
import { isValidPhone, updatePhoneValueSearch } from '@sales-center/common'
import { InfoTooltip } from '@sales-center/components'
import { DraftLeadTypeTooltipContents } from '../DraftLeadTypeTooltip/DraftLeadTypeTooltipContents'
import { WebFormButton } from '@user/features/CampaignTracking/WebForm'
import Stack from '@mui/material/Stack'
import { EntityRecordTypes } from '@user/features/EntityDetails/components/EntityDetailsAdditionalInformation/typings'

interface Props {
  branch: string
  email: string | null
  phone: string | null
  submittedBy: string
  type: string
  webFormCallTrackingEventId: string
  createdTime?: Date
  draftLeadId: number
}

export const DraftLeadInformationList: React.FC<Props> = (props) => {
  const { branch, email, phone, submittedBy, type, draftLeadId } = props

  const [formattedPhone, phoneIsValid] = React.useMemo(() => {
    const formatted = updatePhoneValueSearch(phone ?? '')
    return [formatted, isValidPhone(formatted)]
  }, [phone])

  const typeWeb = type?.toLowerCase() === EntityRecordTypes.web
  const typeImport = type?.toLowerCase() === EntityRecordTypes.import

  return (
    <Stack direction="row" flexWrap="wrap" rowGap={2}>
      {!!email && (
        <ListItem
          title="Email"
          subtitle={
            <>
              {email?.includes(';') ? (
                <a
                  href={`mailto:${email.split(';')?.[0]}`}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id={`draft-lead-email-link-${draftLeadId}`}
                >
                  {email.split(';')?.[0]}
                </a>
              ) : (
                <a
                  href={`mailto:${email}`}
                  target="_blank"
                  rel="noreferrer"
                  data-test-id={`draft-lead-email-link-${draftLeadId}`}
                >
                  {email}
                </a>
              )}
            </>
          }
        />
      )}

      {!!phone && (
        <ListItem
          title="Phone"
          subtitle={
            phoneIsValid ? (
              <a
                href={`tel:${formattedPhone}`}
                data-test-id={`draft-lead-phone-link-${draftLeadId}`}
              >
                {formattedPhone}
              </a>
            ) : (
              formattedPhone
            )
          }
        />
      )}

      <ListItem title="Submitted By" subtitle={submittedBy || '-'} />

      {!!branch && <ListItem title="Branch" subtitle={branch} />}

      <ListItem
        title="Type"
        subtitle={
          <>
            {type} <InfoTooltip infoText={<DraftLeadTypeTooltipContents />} />
          </>
        }
      />

      {!typeImport && (
        <ListItem
          title={typeWeb ? 'Webform Submission' : 'Captured Information'}
          subtitle={
            <WebFormButton
              noIcon
              text={
                typeWeb ? 'View Submitted Webform' : 'View Captured Information'
              }
              variant="link"
              {...props}
            />
          }
        />
      )}
    </Stack>
  )
}

DraftLeadInformationList.displayName = 'DraftLeadInformationList'
